import { BackendFetch } from '@hantera/portal-app'
import parseStatisticsJson from './statistics-json'
import type { ResourceLogging } from './ingresses.types'
import type {
  Egress,
  EgressHostStatistics,
  EgressHostStatisticsParams,
  EgressKeyStatistics,
  EgressStatisticsParams,
} from './egresses.types'

function range(query: URLSearchParams, from?: Date, to?: Date) {
  if (from) query.set('from', from.toISOString())
  if (to) query.set('to', to.toISOString())
}

export default function egresses(fetch: BackendFetch) {
  return {
    async getAll(): Promise<Egress[]> {
      const response = await fetch('/core/resources/egresses', { method: 'GET' })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      return parseStatisticsJson(await response.text())
    },

    async getStatistics(params?: EgressStatisticsParams): Promise<EgressKeyStatistics[]> {
      const query = new URLSearchParams()
      if (params?.egressKey) query.set('egressKey', params.egressKey)
      range(query, params?.from, params?.to)

      const queryString = query.toString()
      const response = await fetch(
        queryString
          ? `/core/resources/egresses/statistics?${queryString}`
          : '/core/resources/egresses/statistics',
        { method: 'GET' },
      )

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      return parseStatisticsJson(await response.text()).egresses ?? []
    },

    /**
     * Outbound traffic grouped by remote host, which answers "is that partner slow?" where the
     * per-egress statistics answer "is this egress slow?".
     *
     * Counts will not match the per-egress totals, and that is correct: an egress that fetches a
     * token and then posts is one egress call and two host calls.
     */
    async getHostStatistics(params?: EgressHostStatisticsParams): Promise<EgressHostStatistics[]> {
      const query = new URLSearchParams()
      if (params?.host) query.set('host', params.host)
      if (params?.transport) query.set('transport', params.transport)
      range(query, params?.from, params?.to)

      const queryString = query.toString()
      const response = await fetch(
        queryString
          ? `/core/resources/egresses/statistics/hosts?${queryString}`
          : '/core/resources/egresses/statistics/hosts',
        { method: 'GET' },
      )

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }

      return parseStatisticsJson(await response.text()).hosts ?? []
    },

    /** @see ingresses.updateLogging — the same reasoning applies to both families. */
    async updateLogging(egressId: string, patch: ResourceLogging): Promise<void> {
      const response = await fetch(`/core/resources/egresses/${egressId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })

      if (!response.ok) {
        const errors = await response.errors()
        throw errors[0]
      }
    },
  }
}
